import { useEffect } from 'react';
import type { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import {
  getStorageKeys,
  safeLocalStorage,
} from '../../../utils/safeLocalStorage';
import { ArDriveProvider } from '../../../utils/upload-providers';
import { createArDriveWalletAdapter } from '../../../utils/wallet';

export interface UseArDrivePendingRecoveryArgs {
  isSelected: boolean;
  wallet: ReturnType<typeof useWallet>;
  trpc: TRPC;
  checkArDriveBalance: () => Promise<void>;
}

export function useArDrivePendingRecovery({
  isSelected,
  wallet,
  trpc,
  checkArDriveBalance,
}: UseArDrivePendingRecoveryArgs): void {
  useEffect(() => {
    if (!wallet.publicKey || !wallet.signMessage || !isSelected) return;

    const checkPendingTransactions = async () => {
      const pendingKeys = getStorageKeys(safeLocalStorage).filter((key) =>
        key.startsWith('ardrive_pending_'),
      );

      if (pendingKeys.length === 0) return;

      console.log(`Found ${pendingKeys.length} pending ArDrive transactions`);

      const provider = new ArDriveProvider();
      const walletAdapter = createArDriveWalletAdapter(wallet, {
        throwOnInvalid: false,
      });

      if (!walletAdapter) return;

      try {
        await provider.initialize({
          walletAdapter,
        });

        for (const key of pendingKeys) {
          const txId = key.replace('ardrive_pending_', '');
          console.log(`Attempting to recover transaction: ${txId}`);

          try {
            // @ts-expect-error - turbo is private but we need to access it
            if (provider.turbo) {
              // @ts-expect-error
              const result = await provider.turbo.submitFundTransaction({
                txId,
              });

              if (result.status !== 'failed') {
                safeLocalStorage.removeItem(key);
                console.log(`Successfully recovered transaction: ${txId}`);

                try {
                  await trpc.client.updateArDriveTopup.mutate({
                    txId,
                    status: 'completed',
                  });
                } catch (updateError) {
                  captureAndReportError(
                    'Failed to update backend status:',
                    updateError,
                  );
                }

                toast.success('Pending ArDrive credits have been applied!', {
                  description: `Transaction ${txId.slice(0, 8)}... has been recovered.`,
                });

                checkArDriveBalance();
              }
            }
          } catch (error) {
            captureAndReportError(
              `Failed to recover transaction ${txId}:`,
              error,
            );
          }
        }
      } catch (error) {
        captureAndReportError(
          'Failed to initialize ArDrive for recovery:',
          error,
        );
      }
    };

    checkPendingTransactions();

    const interval = setInterval(() => {
      const hasPending = getStorageKeys(safeLocalStorage).some((key) =>
        key.startsWith('ardrive_pending_'),
      );
      if (hasPending) {
        checkPendingTransactions();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [wallet.publicKey, isSelected]);
}
